/**
 * Meetings page — CRM control/audit/attribution layer for RingCentral Video.
 *
 * Layout follows the RC Softphone pattern:
 *   • Real users: primary tab = provider-hosted iframe embed (currently the
 *     stub join URL — Sprint 2 will swap in the real RCV embeddable).
 *     If the iframe fails to load, we surface a clear fallback CTA.
 *   • Secondary tab: CRM Meeting Activity — the attributed, role-respecting
 *     list of meetings (never the raw provider dump).
 *   • Demo users: kept on the existing /demo-bookings surface — this page is
 *     simply not in their sidebar, but if they do reach it we render a
 *     non-intrusive notice pointing to Demo Bookings.
 *
 * Role gating:
 *   • read_only sees the page + activity list, but Schedule / Edit / Cancel
 *     controls are hidden. API enforces too (403).
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Video, Calendar, X, Plus, ExternalLink, AlertTriangle, RefreshCw } from "lucide-react";
import { createMeeting, listMeetings, cancelMeeting, markMeetingNoShow } from "@/lib/meetingsApi";
import useConfirm from "@/hooks/useConfirm";

const WRITE_ROLES = new Set(["super_admin", "crm_admin", "sales_manager", "sales_agent"]);

function canWrite(role) {
  return WRITE_ROLES.has(role);
}

function StatusBadge({ status }) {
  const style = {
    scheduled: { bg: "var(--blue-light, #E6F2FF)", fg: "var(--blue, #1A73E8)" },
    completed: { bg: "var(--green-light, #DAFBE8)", fg: "var(--green, #1D9E75)" },
    canceled:  { bg: "var(--gray-light, #EEE)",    fg: "var(--text-secondary, #666)" },
    no_show:   { bg: "#FFF1F0",                    fg: "#A32D2D" },
  }[status] || { bg: "#EEE", fg: "#444" };
  return (
    <span data-testid={`meeting-status-${status}`}
      style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 3,
               background: style.bg, color: style.fg }}>
      {status}
    </span>
  );
}

function ScheduleForm({ onCreated, onCancel, initialLeadId }) {
  const [title, setTitle] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [duration, setDuration] = useState(30);
  const [leadId, setLeadId] = useState(initialLeadId || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) return toast.error("Title is required");
    if (!scheduledAt) return toast.error("Scheduled time is required");
    setBusy(true);
    try {
      const m = await createMeeting({
        title: title.trim(),
        scheduled_at: new Date(scheduledAt).toISOString(),
        duration_min: Number(duration),
        lead_id: leadId.trim() || null,
      });
      toast.success("Meeting scheduled");
      onCreated?.(m);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to schedule meeting");
    }
    setBusy(false);
  };

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }} data-testid="meetings-schedule-form">
      <h3 style={{ fontSize: 14, fontWeight: 650, marginBottom: 10 }}>Schedule new meeting</h3>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 100px 1fr", gap: 10, marginBottom: 10 }}>
        <input className="input" placeholder="Meeting title"
               value={title} onChange={e => setTitle(e.target.value)}
               disabled={busy} data-testid="meetings-new-title" />
        <input className="input" type="datetime-local"
               value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
               disabled={busy} data-testid="meetings-new-when" />
        <input className="input" type="number" min={5} max={480} placeholder="min"
               value={duration} onChange={e => setDuration(e.target.value)}
               disabled={busy} data-testid="meetings-new-duration" />
        <input className="input" placeholder="Lead ID (optional)"
               value={leadId} onChange={e => setLeadId(e.target.value)}
               disabled={busy} data-testid="meetings-new-lead" />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn btn-primary" onClick={submit} disabled={busy}
                data-testid="meetings-new-submit">
          {busy ? "Scheduling…" : "Schedule"}
        </button>
        <button className="btn" onClick={onCancel} disabled={busy}
                data-testid="meetings-new-cancel">Cancel</button>
      </div>
    </div>
  );
}

// RingCentral's `meetings.ringcentral.com` domain refuses iframe embedding
// (CSP) — so for the `rcv` and `rcv_stub` providers we skip the iframe
// attempt entirely and render the provider-approved external-tab CTA.
// This avoids the visible "refused to connect" error that was showing up
// in Sprint 1 and the telemetry log tells ops why the fallback was used.
const NO_IFRAME_PROVIDERS = new Set(["rcv", "rcv_stub"]);

function EmbedTab({ joinUrl, provider }) {
  const [embedFailed, setEmbedFailed] = useState(false);
  const timeoutRef = useRef(null);
  const noIframe = provider && NO_IFRAME_PROVIDERS.has(provider);

  useEffect(() => {
    setEmbedFailed(false);
    if (!joinUrl || noIframe) return;
    // Heuristic for future embeddable providers: if iframe onLoad doesn't
    // fire in 7s, treat as failed (CSP / network / provider outage).
    timeoutRef.current = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn("[meetings] embed failed — timeout; reason=onload_not_fired",
                   { joinUrl, provider });
      setEmbedFailed(true);
    }, 7000);
    return () => clearTimeout(timeoutRef.current);
  }, [joinUrl, noIframe, provider]);

  if (!joinUrl) {
    return (
      <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--text-tertiary)" }}
           data-testid="meetings-embed-empty">
        <Video style={{ width: 28, height: 28, margin: "0 auto 8px" }} />
        <div style={{ fontSize: 13, fontWeight: 600 }}>No active meeting selected</div>
        <div style={{ fontSize: 11, marginTop: 4 }}>Pick a scheduled meeting from CRM Activity to open it here.</div>
      </div>
    );
  }
  // CSP-blocked provider → show the external-tab card directly. Same
  // data-testid as the timeout fallback so existing tests keep working and
  // ops-facing UX stays consistent.
  if (noIframe || embedFailed) {
    const reason = noIframe ? "Provider blocks iframe embedding (CSP)" :
                              "Provider may be blocking iframe embedding";
    return (
      <div className="card" style={{ padding: 24, textAlign: "center" }}
           data-testid="meetings-embed-fallback">
        <AlertTriangle style={{ width: 22, height: 22, color: "var(--amber, #D97706)" }} />
        <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>Open meeting in new tab</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 4 }}>
          {reason}. CRM activity continues to track this meeting.
        </div>
        <a className="btn btn-primary" href={joinUrl} target="_blank" rel="noreferrer"
           style={{ display: "inline-flex", gap: 6, marginTop: 14, fontSize: 12 }}
           data-testid="meetings-embed-external-cta">
          <ExternalLink style={{ width: 12, height: 12 }} /> Open meeting
        </a>
      </div>
    );
  }
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="meetings-embed-frame-wrap">
      <iframe
        title="RC Video meeting"
        src={joinUrl}
        allow="camera; microphone; display-capture; autoplay"
        style={{ width: "100%", height: 540, border: 0 }}
        onLoad={() => clearTimeout(timeoutRef.current)}
        data-testid="meetings-embed-frame"
      />
    </div>
  );
}

function MeetingRow({ m, onSelect, onCancel, onMarkNoShow, role }) {
  const canCancelHere = canWrite(role) && m.status === "scheduled";
  // Phase B (Iter 135): Mark no-show appears only when the meeting is
  // scheduled AND the slot has already ended (now > scheduled_at + duration).
  // Earlier than that it would be premature; later, the auto-sweep will
  // eventually catch it anyway.
  const slotEnded = (() => {
    if (m.status !== "scheduled" || !m.scheduled_at) return false;
    const start = new Date(m.scheduled_at).getTime();
    if (Number.isNaN(start)) return false;
    const end = start + (m.duration_min || 30) * 60 * 1000;
    return Date.now() >= end;
  })();
  const canMarkNoShow = canWrite(role) && slotEnded;
  return (
    <tr style={{ borderBottom: "0.5px solid var(--border)" }}
        data-testid={`meeting-row-${m.id}`}>
      <td style={{ padding: "8px 10px" }}>
        <div style={{ fontSize: 12, fontWeight: 600 }}>{m.title}</div>
        <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{m.provider} · {m.external_meeting_id}</div>
      </td>
      <td style={{ padding: "8px 10px", fontSize: 11, color: "var(--text-secondary)" }}>
        {m.scheduled_at ? new Date(m.scheduled_at).toLocaleString() : "—"}
      </td>
      <td style={{ padding: "8px 10px", fontSize: 11 }}>{m.duration_min}m</td>
      <td style={{ padding: "8px 10px", fontSize: 11 }}>{m.organizer_name || "—"}</td>
      <td style={{ padding: "8px 10px" }}><StatusBadge status={m.status} /></td>
      <td style={{ padding: "8px 10px", textAlign: "right" }}>
        <button className="btn" style={{ fontSize: 10, marginRight: 4 }}
                onClick={() => onSelect(m)}
                data-testid={`meeting-open-${m.id}`}>
          Open
        </button>
        {canMarkNoShow && (
          <button className="btn" style={{ fontSize: 10, marginRight: 4 }}
                  onClick={() => onMarkNoShow(m)}
                  data-testid={`meeting-no-show-${m.id}`}
                  title="The slot has passed but no one joined or the meeting wasn't recorded.">
            No-show
          </button>
        )}
        {canCancelHere && (
          <button className="btn" style={{ fontSize: 10 }}
                  onClick={() => onCancel(m)}
                  data-testid={`meeting-cancel-${m.id}`}>
            <X style={{ width: 10, height: 10 }} /> Cancel
          </button>
        )}
      </td>
    </tr>
  );
}

export default function Meetings() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [params] = useSearchParams();
  const leadFilter = params.get("lead_id") || "";
  const autoSchedule = params.get("schedule") === "1";
  const role = user?.role || "";
  const [tab, setTab] = useState("embed");     // embed | activity
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listMeetings({ limit: 50, lead_id: leadFilter || undefined });
      setMeetings(r?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load meetings");
      setMeetings([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadFilter]);

  // Deep-link: /meetings?lead_id=<id>&schedule=1 auto-opens the schedule form
  // pre-filled with the lead. Writers only — read_only falls through silently.
  useEffect(() => {
    if (autoSchedule && canWrite(role)) setShowForm(true);
  }, [autoSchedule, role]);

  const onCreated = (m) => {
    setShowForm(false);
    setMeetings(prev => [m, ...prev]);
    setSelected(m);
    setTab("embed");
  };

  const onCancel = async (m) => {
    const ok = await confirm({
      title: "Cancel meeting?",
      body: `Cancel "${m.title}"? Invitees will be notified.`,
      confirmLabel: "Cancel meeting",
      confirmTone: "danger",
      testid: "meeting-cancel-confirm",
    });
    if (!ok) return;
    try {
      const updated = await cancelMeeting(m.id, "Cancelled from UI");
      setMeetings(prev => prev.map(x => x.id === m.id ? updated : x));
      if (selected?.id === m.id) setSelected(updated);
      toast.success("Meeting canceled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const onMarkNoShow = async (m) => {
    const ok = await confirm({
      title: "Mark as no-show?",
      body: `Mark "${m.title}" as a no-show? The lead's no-show flag will be set and the AI scoring engine will pick it up.`,
      confirmLabel: "Mark no-show",
      confirmTone: "danger",
      testid: "meeting-no-show-confirm",
    });
    if (!ok) return;
    try {
      const updated = await markMeetingNoShow(m.id, "Marked from UI");
      setMeetings(prev => prev.map(x => x.id === m.id ? updated : x));
      if (selected?.id === m.id) setSelected(updated);
      toast.success("Marked as no-show");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mark no-show");
    }
  };

  const writeOk = canWrite(role);

  return (
    <div style={{ padding: 24 }} data-testid="meetings-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 650 }}>Meetings</h1>
          <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
            RingCentral Video · CRM attribution & audit
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" onClick={load} disabled={loading} style={{ fontSize: 11 }}
                  data-testid="meetings-refresh">
            <RefreshCw style={{ width: 12, height: 12 }} /> Refresh
          </button>
          {writeOk && (
            <button className="btn btn-primary" style={{ fontSize: 12 }}
                    onClick={() => setShowForm(true)}
                    data-testid="meetings-open-schedule">
              <Plus style={{ width: 12, height: 12 }} /> Schedule
            </button>
          )}
        </div>
      </div>

      {showForm && writeOk && (
        <ScheduleForm onCreated={onCreated} onCancel={() => setShowForm(false)}
                      initialLeadId={leadFilter} />
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "0.5px solid var(--border)", marginBottom: 14 }}
           data-testid="meetings-tabs">
        {[
          { key: "embed", label: "Live Meeting" },
          { key: "activity", label: "CRM Meeting Activity" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`meetings-tab-${t.key}`}
            style={{
              padding: "8px 14px", fontSize: 12, fontWeight: 600, border: "none", background: "transparent",
              cursor: "pointer",
              color: tab === t.key ? "var(--blue, #1A73E8)" : "var(--text-secondary)",
              borderBottom: tab === t.key ? "2px solid var(--blue, #1A73E8)" : "2px solid transparent",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "embed" && <EmbedTab joinUrl={selected?.join_url} provider={selected?.provider} />}

      {tab === "activity" && (
        <div className="card" style={{ padding: 0 }} data-testid="meetings-activity-panel">
          {loading ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12 }}>
              Loading…
            </div>
          ) : meetings.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12 }}
                 data-testid="meetings-activity-empty">
              No meetings yet.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "0.5px solid var(--border)" }}>
                  {["Title", "Scheduled", "Duration", "Organizer", "Status", ""].map(h => (
                    <th key={h} style={{ padding: "8px 10px", fontSize: 10, fontWeight: 600,
                                         color: "var(--text-tertiary)", textAlign: "left", textTransform: "uppercase" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {meetings.map(m => (
                  <MeetingRow key={m.id} m={m} role={role}
                              onSelect={(x) => { setSelected(x); setTab("embed"); }}
                              onCancel={onCancel}
                              onMarkNoShow={onMarkNoShow} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
